import { DatePicker, Select } from "antd";
import { useState } from "react";
import useSelect from "../../../hooks/useSelect";
import {
  useGetOverviewQuery,
  useGetTopDealersQuery,
} from "../../../features/dashboard/dashboardApi";
import useError from "../../../hooks/useError";
import TotalDealersIcon from "../../../assets/icons/TotalDealersIcon";
import TotalAmountIcon from "../../../assets/icons/TotalAmountIcon";
import TotalTransactionsIcon from "../../../assets/icons/TotalTransactionsIcon";
import AppLayout from "../../../layouts/AppLayout";
import { formatSouthAsianNumber } from "../../../utils/utils";
import TransactionsChart from "../../../components/charts/TransactionsChart";
import {
  useGetDashboardTopTradersQuery,
  useGetTraderListQuery,
} from "../../../features/trader/traderApi";
import TopDealers from "../../../components/dashboard/TopDealers";
import TopTraders from "../../../components/dashboard/TopTraders";
import { TraderDetailsData } from "../../../types/trader.type";
import { Dayjs } from "dayjs";

export default function Dashboard() {
  const [selectedCategory, setSelectedCategory] = useState("overall");
  const [activeButton, setActiveButton] = useState("dealers");
  const category =
    selectedCategory === "overall" ? activeButton : selectedCategory;

  const {
    dateFilter: topDealersDate,
    select: topDealersSelect,
    handleDateChange,
  } = useSelect();
  const handleSelectChange = (value: any) => {
    setSelectedCategory(value);
  };

  const {
    data: overviewData,
    isLoading: isOverviewLoading,
    isError: isOverviewError,
    error: overviewError,
  } = useGetOverviewQuery({
    params: {
      ...topDealersDate,
    },
  });

  const { data: topDealersData, isLoading: isTopDealersDataLoading } =
    useGetTopDealersQuery({
      params: { ...(topDealersSelect ? { ...topDealersDate } : {}) },
    });

  const { data: topTradersData, isLoading: isTopTradersLoading } =
    useGetDashboardTopTradersQuery({});

  const { data: treaders, isLoading: treadersLoading } = useGetTraderListQuery({
    ...topDealersDate,
  });

  function totalTreaderAmount(item: keyof TraderDetailsData) {
    let totalAmount;
    totalAmount = formatSouthAsianNumber(
      treaders?.data?.rows?.reduce((acc, curr) => {
        return acc + Number(curr?.[item]) || 0;
      }, 0) || 0
    );

    return totalAmount;
  }

  useError(isOverviewError, overviewError, "Get overview failed");

  const getTopTitle = () => {
    if (selectedCategory === "dealers") {
      return "Top 10 Dealers";
    }
    if (selectedCategory === "traders") {
      return "Top 10 Traders";
    }
    if (selectedCategory === "overall") {
      return activeButton === "traders" ? "Top 10 Traders" : "Top 10 Dealers";
    }
    return "";
  };

  const overviewDealersCardsData = [
    {
      id: 1,
      icon: TotalDealersIcon,
      label: "Total Dealers",
      amount: overviewData?.data.totalDealers || 0,
    },
    {
      id: 2,
      icon: TotalAmountIcon,
      label: "Total Amount of Deals",
      amount: overviewData?.data.totalAmount || 0,
    },
    {
      id: 3,
      icon: TotalTransactionsIcon,
      label: "Total Transactions",
      amount: overviewData?.data.totalTransactions || 0,
    },
  ];

  const overviewTreaderCardsData = [
    {
      id: 1,
      icon: TotalDealersIcon,
      label: "Total Treaders",
      amount: treaders?.data.count || 0,
    },
    {
      id: 2,
      icon: TotalAmountIcon,
      label: "Total Amount of Deals",
      amount: totalTreaderAmount("tradersToSupplierTransactionAmount") || 0,
    },
    {
      id: 3,
      icon: TotalTransactionsIcon,
      label: "Total Transactions",
      amount: totalTreaderAmount("tradersToSupplierTransactionCount") || 0,
    },
  ];

  const renderTopContent = () => {
    if (category === "dealers") {
      return (
        <TopDealers
          isLoading={isTopDealersDataLoading}
          topDealersData={topDealersData}
        />
      );
    }

    if (category === "traders") {
      return (
        <TopTraders isLoading={isTopTradersLoading} data={topTradersData} />
      );
    }

    return null;
  };

  return (
    <AppLayout title="Dashboard">
      <div>
        <div className="flex justify-between items-center">
          <div className="flex justify-start items-center gap-5">
            <div className="self-stretch justify-start !text-[#2D3643] text-4xl font-medium ">
              Overview
            </div>
            <Select
              showSearch
              placeholder="Select a person"
              optionFilterProp="label"
              // allowClear
              defaultValue="overall"
              onChange={handleSelectChange}
              options={[
                { value: "overall", label: "Overall" },
                { value: "dealers", label: "Dealers" },
                { value: "traders", label: "Traders" },
              ]}
            />
          </div>

          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              {/* <DatePicker allowClear /> */}
              <DatePicker
                allowClear
                picker="week"
                onChange={(date: Dayjs | null) => {
                  if (!date) {
                    return;
                  }

                  // depending on your locale, week may start on Sunday or Monday:
                  const startOfWeek = date.startOf("week");
                  const endOfWeek = date.endOf("week");
                  handleDateChange([startOfWeek, endOfWeek]);
                }}
              />
            </div>
          </div>
        </div>

        {/* Dealers Section */}
        {(selectedCategory === "overall" || selectedCategory === "dealers") && (
          <div className="mb-3">
            <div className="justify-start !text-[#2D3643] text-base font-medium uppercase mb-3 mt-3">
              Dealers
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mt-4">
              {overviewDealersCardsData.map((card, index) => (
                <div
                  className="p-4 rounded-md dark:bg-primaryBackground bg-white dark:border-others-body border-others-borderLight border border-solid"
                  key={card.id}
                >
                  <div className="flex gap-4 items-center dark:text-[#E9EFF7] text-others-table-header">
                    <card.icon />
                    <span className="text-others-header dark:text-header">
                      {card.label}
                    </span>
                  </div>
                  <div className="text-4xl mt-3 dark:text-header text-others-table-header">
                    {index === 1
                      ? "৳" + formatSouthAsianNumber(Number(card.amount) || 0)
                      : formatSouthAsianNumber(Number(card.amount) || 0)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Traders Section */}
        {(selectedCategory === "overall" || selectedCategory === "traders") && (
          <div className="mb-3">
            <div className="justify-start !text-[#2D3643] text-base font-medium uppercase mb-3 mt-3">
              Traders
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mt-4">
              {overviewTreaderCardsData.map((card, index) => (
                <div
                  className="p-4 rounded-md dark:bg-primaryBackground bg-white dark:border-others-body border-others-borderLight border border-solid"
                  key={card.id}
                >
                  <div className="flex gap-4 items-center dark:text-[#E9EFF7] text-others-table-header">
                    <card.icon />
                    <span className="text-others-header dark:text-header">
                      {card.label}
                    </span>
                  </div>
                  <div className="text-4xl mt-3 dark:text-header text-others-table-header">
                    {index === 1 ? "৳" + (card.amount || 0) : card.amount || 0}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mt-6 items-start">
          <TransactionsChart selectedCategory={selectedCategory} />
          <div className="lg:col-span-1 w-full p-4 rounded-md bg-white dark:bg-primaryBackground dark:border-others-body border-others-borderLight border border-solid">
            <div className="flex gap-2 my-2 justify-between items-center">
              <div className="text-lg dark:text-header text-others-table-header">
                {getTopTitle()}
              </div>
              {selectedCategory === "overall" && (
                <div className="h-10 px-1.5 rounded-lg outline outline-1 outline-offset-[-1px] outline-slate-400 inline-flex justify-start items-center gap-2">
                  {/* Dealers Button */}
                  <button
                    onClick={() => {
                      setActiveButton("dealers");
                    }}
                    className={`h-8 px-1.5 rounded-md flex justify-start items-center text-base font-medium cursor-pointer ${
                      activeButton === "dealers"
                        ? "bg-blue-600 text-white !border-none !hover:border-none !hover:text-white"
                        : "!bg-transparent !border-none !hover:bg-transparent !hover:border-none text-slate-700"
                    }`}
                  >
                    Dealers
                  </button>

                  {/* Traders Button */}
                  <button
                    onClick={() => {
                      setActiveButton("traders");
                    }}
                    className={`h-8 px-1.5 rounded-md flex justify-start items-center text-base font-medium cursor-pointer ${
                      activeButton === "traders"
                        ? "bg-blue-600 text-white !border-none !hover:border-none !hover:text-white"
                        : "!bg-transparent !border-none !hover:bg-transparent !hover:border-none text-slate-700"
                    }`}
                  >
                    Traders
                  </button>
                </div>
              )}
            </div>

            {renderTopContent()}
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
